import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
} from '@bb-ui/react-library';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { Trash } from '@bb-ui/icons/dist/medium/Trash';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'hooks/useSnackbar';
import { useRestApi } from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { useCohortDetailLogic } from '../hooks/useCohortDetailLogic';
import { NotificationResponse, SiteInCohort, CohortDetail } from 'App.types';
import { PaginationControls } from '../shared';
import FilterSitesDialog from '../shared/FilterSitesDialog';
import ScheduleConflictDialog from '../CreateCohortDialog/ScheduleConflictDialog';
import {
  CohortSummaryCard,
  NotificationConfirmDialog,
  ReminderSitesDialog,
  SitesTable,
  SitesTableToolbar,
  CohortExecutionControls,
  RetryPresyncPopover,
} from './components';
import { useCohortsActions } from '../hooks/useCohortsActions';
import { useCohortExecution } from '../hooks/useCohortExecution';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { isCohortEditable, normalizeCohortStatus, getCohortPhase, handleApiError } from '../utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    errorText: {
      color: theme.palette.error.main,
      textAlign: 'center',
      marginTop: theme.spacing(2),
    },
    confirmDialog: {
      '& .MuiDialog-paper': {
        minWidth: 500,
      },
    },
    warningContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    warningText: {
      color: theme.palette.warning.main,
      fontWeight: 600,
    },
    siteInfo: {
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.shape.borderRadius,
      fontFamily: 'monospace',
    },
    deleteWarning: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.error.light,
      borderRadius: theme.shape.borderRadius,
      border: `2px solid ${theme.palette.error.main}`,
    },
    deleteWarningText: {
      color: theme.palette.error.main,
      fontWeight: 600,
    },
    dialogActions: {
      padding: theme.spacing(2, 3),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    leftActions: {
      display: 'flex',
      alignItems: 'center',
    },
    rightActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    deleteButton: {
      color: theme.palette.error.main,
      '&:disabled': {
        color: theme.palette.grey[400],
      },
    },
    contentTransition: {
      transition: 'opacity 0.2s ease',
    },
    contentRefreshing: {
      opacity: 0.8,
    },
  }),
);

interface CohortDetailDialogProps {
  open: boolean;
  cohortId: string | null;
  onClose: () => void;
  onCohortDeleted?: () => void;
}

const CohortDetailDialog: React.FC<CohortDetailDialogProps> = ({
  open,
  cohortId,
  onClose,
  onCohortDeleted,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedRegion, cohorts } = useCohortsContext();

  const {
    deleteCohort,
    removeSiteFromCohort,
    addSiteToACohort,
    updateCohortDowntime,
    refreshCohorts,
    loadingDelete,
    loadingUpdate,
    loadingAddSite,
    loadingRemoveSite,
  } = useCohortsActions();

  const { retrySiteExecution, clearRetryState, isSiteRetrying, retryingSiteIds } =
    useCohortExecution();

  const [sites, setSites] = useState<SiteInCohort[]>([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRemovingSiteLocal, setIsRemovingSiteLocal] = useState(false);
  const [isRefreshingCohortStatus, setIsRefreshingCohortStatus] = useState(false);
  const sitesRefreshSequenceRef = useRef(0);
  const [pendingDowntimeDate, setPendingDowntimeDate] = useState<string | null>(null);
  const [downtimeConflictDialogOpen, setDowntimeConflictDialogOpen] = useState(false);
  const [downtimeConflictingSites, setDowntimeConflictingSites] = useState<SiteInCohort[]>([]);
  const [retryAnchorEl, setRetryAnchorEl] = useState<HTMLElement | null>(null);
  const [retrySiteInfo, setRetrySiteInfo] = useState<{
    captainId: string;
    learnUrl?: string;
  } | null>(null);

  const cohort = useMemo(() => cohorts.find((c) => c.id === cohortId), [cohorts, cohortId]);

  const { data: cohortData, fetch: fetchCohort } = useRestApi<CohortDetail>(
    cohortId ? apiUrl('lct', `regions/${selectedRegion}/cohort/${cohortId}`) : '',
    { manual: true },
  );

  const cohortDetail = useMemo((): CohortDetail | null => {
    if (cohortData) {
      return {
        ...cohortData,
        downtime_date: cohort?.downtime_date ?? cohortData.downtime_date,
        sites,
      };
    }

    if (!cohort) return null;
    return {
      ...cohort,
      fleet_id: cohort.fleet_id || '',
      fleet_account_id: cohort.fleet_account_id || '',
      fleet_region: cohort.fleet_region || '',
      sites,
    };
  }, [cohortData, cohort, sites]);

  const { data: sitesData, fetch: fetchSites } = useRestApi<{ items: SiteInCohort[] }>(
    cohortId ? apiUrl('lct', `regions/${selectedRegion}/search`) : '',
    {
      manual: true,
    },
  );

  const { doPost: sendNotifications, loadingRequests: loadingNotificationsApi } =
    useRestApi<NotificationResponse>(
      cohortId ? apiUrl('lct', `regions/${selectedRegion}/notifications`) : '',
      {
        manual: true,
      },
    );

  const {
    pageIndex,
    visibleSitesCount,
    totalPages,
    siteToRemoveInfo,
    confirmDialogOpen,
    reminderDialogOpen,
    removeSiteConfirmOpen,
    deleteCohortConfirmOpen,
    searchText,
    statusFilter,
    sortConfig,
    availableStatuses,
    hasActiveFilters,
    handleNotificationClick,
    handleReminderClick,
    handleRemoveSiteRequest,
    handleDeleteCohortRequest,
    setPageIndex,
    setConfirmDialogOpen,
    setReminderDialogOpen,
    setRemoveSiteConfirmOpen,
    setDeleteCohortConfirmOpen,
    setSearchText,
    setStatusFilter,
    handleSort,
    getVisiblePaginatedSites,
    getSitesForComponents,
    fetchJobStatuses,
  } = useCohortDetailLogic({
    cohortId,
    sites,
    pageSize: 5,
    enableNotifications: cohortDetail?.enable_notifications,
  });

  useEffect(() => {
    setLoadingNotifications(loadingNotificationsApi);
    setLoadingReminders(loadingNotificationsApi);
  }, [loadingNotificationsApi]);

  useEffect(() => {
    if (open && cohortId) {
      setIsInitialLoad(true);
      fetchCohort().catch((error: any) => {
        handleApiError(error, { operation: 'loadCohortDetail', enqueueSnackbar, t });
      });
      fetchSites({
        params: {
          cohort: cohortId,
          results_count: 100,
        },
      }).catch((error: any) => {
        handleApiError(error, { operation: 'loadSites', enqueueSnackbar, t });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cohortId, fetchSites, fetchCohort]);

  useEffect(() => {
    if (sitesData?.items) {
      setSites(sitesData.items);
      setIsInitialLoad(false);
    }
  }, [sitesData]);

  useEffect(() => {
    if (!open) {
      setSites([]);
      setIsInitialLoad(true);
      setPendingDowntimeDate(null);
      setDowntimeConflictDialogOpen(false);
      setDowntimeConflictingSites([]);
    }
  }, [open]);

  useEffect(() => {
    if (sites.length > 0 && cohortDetail?.migration_status) {
      fetchJobStatuses(sites, cohortDetail.migration_status);
    }
  }, [sites, cohortDetail?.migration_status, fetchJobStatuses]);

  const refreshLocalSites = useCallback(async (): Promise<SiteInCohort[]> => {
    if (!cohortId) return [];
    try {
      const response = await fetchSites({
        params: {
          cohort: cohortId,
          results_count: 100,
        },
      });
      return response?.data?.items || [];
    } catch {
      return [];
    }
  }, [cohortId, fetchSites]);

  const canSendReminders = useMemo(() => {
    const status = cohortDetail?.migration_status;
    const phase = getCohortPhase(status);
    return phase === 'notification' && !isCohortEditable(status);
  }, [cohortDetail?.migration_status]);

  const waitForStatusChange = useCallback(
    async (currentStatus: string | null | undefined) => {
      const maxAttempts = 10;
      const delay = 2000;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, delay);
        });

        try {
          // eslint-disable-next-line no-await-in-loop
          const response: any = await fetchCohort({
            params: { _t: Date.now() },
          });

          let newStatus;
          if (response?.data && 'migration_status' in response.data) {
            newStatus = response.data.migration_status;
          } else if (response && 'migration_status' in response) {
            newStatus = response.migration_status;
          }

          if (newStatus !== undefined && newStatus !== currentStatus) {
            return newStatus;
          }
        } catch {
          // Silently handle polling errors
        }
      }
      return null;
    },
    [fetchCohort],
  );

  const handleExecuteSuccess = useCallback(async () => {
    const baselineStatus = cohortDetail?.migration_status;
    setIsRefreshingCohortStatus(true);
    await waitForStatusChange(baselineStatus);
    const updatedSites = await refreshLocalSites();
    setSites(updatedSites);
    await refreshCohorts();
    setIsRefreshingCohortStatus(false);
  }, [refreshLocalSites, refreshCohorts, waitForStatusChange, cohortDetail?.migration_status]);

  const handleCancelSuccess = useCallback(async () => {
    const baselineStatus = cohortDetail?.migration_status;
    setIsRefreshingCohortStatus(true);
    await waitForStatusChange(baselineStatus);
    const updatedSites = await refreshLocalSites();
    setSites(updatedSites);
    await refreshCohorts();
    setIsRefreshingCohortStatus(false);
  }, [refreshLocalSites, refreshCohorts, waitForStatusChange, cohortDetail?.migration_status]);

  const handleSendNotifications = useCallback(async () => {
    const baselineStatus = cohortDetail?.migration_status;
    try {
      const payload = {
        captain_ids: getSitesForComponents().map((site) => site.captain_id),
        notifications: {
          status: 'SENT',
        },
      };
      await sendNotifications(payload);
      enqueueSnackbar(t('cohorts.notificationsSent'), { variant: 'success' });
      setConfirmDialogOpen(false);
      setIsRefreshingCohortStatus(true);
      await waitForStatusChange(baselineStatus);
      const updatedSites = await refreshLocalSites();
      setSites(updatedSites);
      await refreshCohorts();
    } catch (error: any) {
      handleApiError(error, { operation: 'sendNotifications', enqueueSnackbar, t });
    } finally {
      setIsRefreshingCohortStatus(false);
    }
  }, [
    sendNotifications,
    enqueueSnackbar,
    t,
    setConfirmDialogOpen,
    getSitesForComponents,
    refreshLocalSites,
    refreshCohorts,
    waitForStatusChange,
    cohortDetail?.migration_status,
  ]);

  const handleSendReminders = useCallback(
    async (selectedSiteIds: string[]) => {
      try {
        const payload = {
          captain_ids: selectedSiteIds,
          notifications: {
            status: 'SENT',
          },
        };
        await sendNotifications(payload);
        enqueueSnackbar(
          `${t('cohorts.remindersSentTo')} ${selectedSiteIds.length} ${t('cohorts.sitesSuccessfully')}`,
          { variant: 'success' },
        );
        setReminderDialogOpen(false);
        setIsRefreshingCohortStatus(true);
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 5000);
        });
        await fetchCohort({ params: { _t: Date.now() } });
        const updatedSites = await refreshLocalSites();
        setSites(updatedSites);
        await refreshCohorts();
      } catch (error: any) {
        handleApiError(error, { operation: 'sendReminders', enqueueSnackbar, t });
      } finally {
        setIsRefreshingCohortStatus(false);
      }
    },
    [
      sendNotifications,
      enqueueSnackbar,
      t,
      setReminderDialogOpen,
      fetchCohort,
      refreshLocalSites,
      refreshCohorts,
    ],
  );

  const handleToggleSite = useCallback(
    async (siteId: string) => {
      if (!cohortId) return;
      const isCurrentlyInCohort = sites.some((site) => site.captain_id === siteId);
      try {
        if (isCurrentlyInCohort) {
          await removeSiteFromCohort(siteId);
          enqueueSnackbar(t('cohorts.siteRemovedSuccessfully'), { variant: 'success' });
        } else {
          await addSiteToACohort(siteId, cohortId);
          enqueueSnackbar(t('cohorts.siteAddedSuccessfully'), {
            variant: 'success',
          });
        }
        setIsRefreshingCohortStatus(true);
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 5000);
        });
        await fetchCohort({ params: { _t: Date.now() } });
        const updatedSites = await refreshLocalSites();
        setSites(updatedSites);
        await refreshCohorts();
      } catch (error: any) {
        const operation = isCurrentlyInCohort ? 'removeSite' : 'addSite';
        handleApiError(error, { operation, enqueueSnackbar, t });
      } finally {
        setIsRefreshingCohortStatus(false);
      }
    },
    [
      cohortId,
      sites,
      removeSiteFromCohort,
      addSiteToACohort,
      enqueueSnackbar,
      t,
      fetchCohort,
      refreshLocalSites,
      refreshCohorts,
    ],
  );

  const handleRetrySite = useCallback(
    (siteId: string, anchorEl: HTMLElement) => {
      if (!cohortId) return;
      setRetrySiteInfo({ captainId: siteId });
      setRetryAnchorEl(anchorEl);
    },
    [cohortId],
  );

  const handleRetryConfirm = useCallback(
    async (siteId: string, hours: number) => {
      if (!cohortId) return;

      setRetryAnchorEl(null);
      setRetrySiteInfo(null);

      const success = await retrySiteExecution(cohortId, siteId, hours);
      if (!success) return;

      try {
        const refreshSequence = sitesRefreshSequenceRef.current + 1;
        sitesRefreshSequenceRef.current = refreshSequence;
        const updatedSites = await refreshLocalSites();
        if (refreshSequence === sitesRefreshSequenceRef.current) {
          setSites(updatedSites);
        }
        await fetchCohort({ params: { _t: Date.now() } });
        await refreshCohorts();
      } finally {
        clearRetryState(siteId);
      }
    },
    [cohortId, retrySiteExecution, clearRetryState, refreshLocalSites, refreshCohorts, fetchCohort],
  );

  const applyDowntimeUpdate = useCallback(
    async (newDateValue: string) => {
      if (!cohortId || !newDateValue) return;
      try {
        const isoDate = new Date(newDateValue).toISOString();
        await updateCohortDowntime(cohortId, { downtime_date: isoDate });
      } catch {
        // Error handling for downtime update
      }
    },
    [cohortId, updateCohortDowntime],
  );

  const handleDowntimeDateChange = useCallback(
    async (newDateValue: string) => {
      await applyDowntimeUpdate(newDateValue);
    },
    [applyDowntimeUpdate],
  );

  const handleDowntimeConflictDetected = useCallback(
    (pendingDate: string, conflicting: SiteInCohort[]) => {
      setPendingDowntimeDate(pendingDate);
      setDowntimeConflictingSites(conflicting);
      setDowntimeConflictDialogOpen(true);
    },
    [],
  );

  const handleDowntimeConflictClose = useCallback(() => {
    setDowntimeConflictDialogOpen(false);
    setDowntimeConflictingSites([]);
    setPendingDowntimeDate(null);
  }, []);

  const handleDowntimeConflictRemoveSite = useCallback(
    (siteId: string) => {
      if (cohortId) {
        removeSiteFromCohort(siteId)
          .then(() => {
            enqueueSnackbar(t('cohorts.siteRemovedSuccessfully'), { variant: 'success' });
            return refreshLocalSites();
          })
          .then((updatedSites) => {
            setSites(updatedSites);
            setDowntimeConflictingSites((prev) => {
              const updated = prev.filter((s) => s.captain_id !== siteId);
              if (updated.length === 0) {
                setDowntimeConflictDialogOpen(false);
                if (pendingDowntimeDate) {
                  applyDowntimeUpdate(pendingDowntimeDate);
                  setPendingDowntimeDate(null);
                }
              }
              return updated;
            });
            return refreshCohorts();
          })
          .catch((error: any) => {
            handleApiError(error, { operation: 'removeSite', enqueueSnackbar, t });
          });
      }
    },
    [
      cohortId,
      removeSiteFromCohort,
      refreshLocalSites,
      refreshCohorts,
      enqueueSnackbar,
      t,
      pendingDowntimeDate,
      applyDowntimeUpdate,
    ],
  );

  const handleConfirmRemoveSite = useCallback(async () => {
    if (!siteToRemoveInfo || !cohortId) return;
    setRemoveSiteConfirmOpen(false);
    setIsRemovingSiteLocal(true);
    try {
      await removeSiteFromCohort(siteToRemoveInfo.captain_id);
      enqueueSnackbar(t('cohorts.siteRemovedSuccessfully'), { variant: 'success' });
      setIsRefreshingCohortStatus(true);
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 5000);
      });
      await fetchCohort({ params: { _t: Date.now() } });
      const updatedSites = await refreshLocalSites();
      setSites(updatedSites);
      await refreshCohorts();
    } catch (error: any) {
      handleApiError(error, { operation: 'removeSite', enqueueSnackbar, t });
    } finally {
      setIsRemovingSiteLocal(false);
      setIsRefreshingCohortStatus(false);
    }
  }, [
    siteToRemoveInfo,
    cohortId,
    removeSiteFromCohort,
    fetchCohort,
    refreshLocalSites,
    refreshCohorts,
    enqueueSnackbar,
    t,
    setRemoveSiteConfirmOpen,
  ]);

  const handleConfirmDeleteCohort = useCallback(async () => {
    if (!cohortId) return;
    try {
      setDeleteCohortConfirmOpen(false);
      onCohortDeleted?.();
      await deleteCohort(cohortId);
    } catch (error: any) {
      if (
        error?.response?.status === 400 &&
        error?.message?.toLowerCase().includes('sites assigned')
      ) {
        enqueueSnackbar(t('cohorts.deleteErrorStillHasSites'), { variant: 'error' });
      } else {
        handleApiError(error, { operation: 'deleteCohort', enqueueSnackbar, t });
      }
    }
  }, [cohortId, deleteCohort, enqueueSnackbar, t, onCohortDeleted, setDeleteCohortConfirmOpen]);

  const hasActiveOperations = loadingRemoveSite || loadingAddSite || loadingDelete;

  const handleClose = useCallback(() => {
    if (!hasActiveOperations) {
      onClose();
    }
  }, [onClose, hasActiveOperations]);

  const getTooltipTitle = (): string => {
    if (loadingDelete) {
      return String(t('cohorts.deleting'));
    }
    return String(t('cohorts.deleteCohort'));
  };

  const getDeleteButtonContent = (): string => {
    const baseText = String(t('cohorts.deleteCohort'));
    if (visibleSitesCount > 0) {
      return `${baseText} (${visibleSitesCount} ${t('cohorts.sites')})`;
    }
    return baseText;
  };

  const canDeleteCohort = isCohortEditable(cohortDetail?.migration_status);

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [selectedDisplayTimezone, setSelectedDisplayTimezone] = useState<string | undefined>(
    undefined,
  );
  const [selectedDisplayTimezoneLabel, setSelectedDisplayTimezoneLabel] = useState<string>(
    t('cohorts.local'),
  );

  const handleSelectedTimezoneChange = useCallback(
    ({ timeZone, label }: { timeZone: string | undefined; label: string }) => {
      setSelectedDisplayTimezone(timeZone);
      setSelectedDisplayTimezoneLabel(label);
    },
    [],
  );

  if (!open || !cohortDetail) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={!hasActiveOperations ? handleClose : undefined}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          onClose={!hasActiveOperations ? handleClose : undefined}
          closeLabel={t('global.close')}
        >
          {t('cohorts.manageCohort')}
        </DialogTitle>
        <DialogContent>
          <div className={classes.contentTransition}>
            <CohortSummaryCard
              cohort={cohortDetail}
              isDowntimeDisabled={!isCohortEditable(cohortDetail.migration_status)}
              onUpdateDowntime={handleDowntimeDateChange}
              onDowntimeConflictDetected={handleDowntimeConflictDetected}
              onSendNotifications={handleNotificationClick}
              onSendReminders={canSendReminders ? handleReminderClick : undefined}
              loadingNotifications={loadingNotifications}
              loadingReminders={loadingReminders}
              loadingDowntime={loadingUpdate}
              sites={getSitesForComponents()}
              isLoadingSites={isInitialLoad}
              isRefreshingStatus={isRefreshingCohortStatus}
              enableNotifications={cohortDetail.enable_notifications}
              onSelectedTimezoneChange={handleSelectedTimezoneChange}
            />

            <SitesTableToolbar
              searchText={searchText}
              onSearchChange={setSearchText}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              availableStatuses={availableStatuses}
              disabled={isInitialLoad}
            />

            <SitesTable
              sites={getVisiblePaginatedSites()}
              onRemoveSite={handleRemoveSiteRequest}
              onRetrySite={handleRetrySite}
              cohortStatus={normalizeCohortStatus(cohortDetail.migration_status)}
              upgradeWorkflowStatus={cohortDetail.upgrade_workflow_status}
              visibleSitesCount={visibleSitesCount}
              cohortId={cohortDetail.id}
              downtimeDate={cohortDetail.downtime_date}
              loadingRemoveSite={isRemovingSiteLocal}
              isLoadingSites={isInitialLoad}
              retryingSiteIds={retryingSiteIds}
              enableNotifications={cohortDetail.enable_notifications}
              timeZone={selectedDisplayTimezone}
              sortConfig={sortConfig}
              onSort={handleSort}
              hasActiveFilters={hasActiveFilters}
            />

            <PaginationControls
              currentPage={pageIndex}
              totalPages={totalPages}
              onPageChange={setPageIndex}
              showAddSites={true}
              onAddSites={() => setFilterDialogOpen(true)}
              isLocked={!isCohortEditable(cohortDetail.migration_status)}
              isDisabled={hasActiveOperations || isInitialLoad}
            />
          </div>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <div className={classes.leftActions}>
            {canDeleteCohort && (
              <Tooltip title={getTooltipTitle()}>
                <IconButton
                  onClick={handleDeleteCohortRequest}
                  disabled={hasActiveOperations}
                  className={classes.deleteButton}
                  aria-label="Delete cohort"
                >
                  <Trash />
                </IconButton>
              </Tooltip>
            )}
          </div>

          <div className={classes.rightActions}>
            {cohortDetail && (
              <CohortExecutionControls
                cohort={cohortDetail}
                onExecuteSuccess={handleExecuteSuccess}
                onCancelSuccess={handleCancelSuccess}
                isDisabled={hasActiveOperations || isInitialLoad}
              />
            )}

            <PrimaryButton onClick={handleClose} disabled={hasActiveOperations}>
              {t('global.close')}
            </PrimaryButton>
          </div>
        </DialogActions>
      </Dialog>

      <FilterSitesDialog
        open={filterDialogOpen && !hasActiveOperations}
        onClose={() => setFilterDialogOpen(false)}
        selectedSiteIds={new Set(sites.map((site) => site.captain_id))}
        onToggleSite={handleToggleSite}
        requiredFleetId={cohortDetail?.fleet_id}
        requiredAccountId={cohortDetail?.fleet_account_id}
        cohortDowntimeDate={cohortDetail?.downtime_date}
        timeZone={selectedDisplayTimezone}
      />

      <NotificationConfirmDialog
        open={confirmDialogOpen && !hasActiveOperations}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleSendNotifications}
        downtimeDate={cohortDetail?.downtime_date || null}
        sitesCount={visibleSitesCount}
        loading={loadingNotifications}
      />

      <ReminderSitesDialog
        open={reminderDialogOpen && !hasActiveOperations}
        onClose={() => setReminderDialogOpen(false)}
        onSendReminders={handleSendReminders}
        sites={getSitesForComponents()}
        loading={loadingReminders}
      />

      <ScheduleConflictDialog
        open={downtimeConflictDialogOpen}
        onClose={handleDowntimeConflictClose}
        conflictingSites={downtimeConflictingSites}
        onRemoveSite={handleDowntimeConflictRemoveSite}
        timezone={selectedDisplayTimezone ?? userTimezone}
        timezoneLabel={selectedDisplayTimezoneLabel}
      />

      <RetryPresyncPopover
        anchorEl={retryAnchorEl}
        onClose={() => {
          setRetryAnchorEl(null);
          setRetrySiteInfo(null);
        }}
        onConfirm={handleRetryConfirm}
        siteId={retrySiteInfo?.captainId ?? null}
        downtimeDate={cohortDetail?.downtime_date ?? null}
        isRetrying={retrySiteInfo ? isSiteRetrying(retrySiteInfo.captainId) : false}
      />

      <Dialog
        open={removeSiteConfirmOpen && !hasActiveOperations}
        onClose={() => !loadingRemoveSite && setRemoveSiteConfirmOpen(false)}
        className={classes.confirmDialog}
      >
        <DialogTitle
          onClose={() => !loadingRemoveSite && setRemoveSiteConfirmOpen(false)}
          closeLabel={t('global.close')}
        >
          {t('cohorts.confirmSiteRemoval')}
        </DialogTitle>
        <DialogContent>
          <div className={classes.warningContent}>
            <Typography className={classes.warningText}>
              {t('cohorts.removeSiteWarning')}
            </Typography>

            {siteToRemoveInfo && (
              <div className={classes.siteInfo}>
                <Typography variant="body2">
                  <strong>{t('cohorts.captainId')}:</strong> {siteToRemoveInfo.captain_id}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('cohorts.learnUrl')}:</strong>{' '}
                  {siteToRemoveInfo.learn_url || t('global.notApplicable')}
                </Typography>
              </div>
            )}

            <Typography variant="body2">{t('cohorts.removeSiteDescription')}</Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <DefaultButton
            onClick={() => setRemoveSiteConfirmOpen(false)}
            disabled={loadingRemoveSite}
          >
            {t('global.cancel')}
          </DefaultButton>
          <PrimaryButton
            onClick={handleConfirmRemoveSite}
            disabled={loadingRemoveSite}
            color="primary"
          >
            {loadingRemoveSite ? t('cohorts.removing') : t('cohorts.removeSite')}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteCohortConfirmOpen}
        onClose={() => !loadingDelete && setDeleteCohortConfirmOpen(false)}
        className={classes.confirmDialog}
      >
        <DialogTitle
          onClose={() => !loadingDelete && setDeleteCohortConfirmOpen(false)}
          closeLabel={t('global.close')}
        >
          {t('cohorts.deleteCohort')}
        </DialogTitle>
        <DialogContent>
          <div className={classes.warningContent}>
            <div className={classes.deleteWarning}>
              <Typography className={classes.deleteWarningText}>
                {t('cohorts.cannotBeUndone')}
              </Typography>
            </div>

            <Typography variant="body1">{t('cohorts.deleteCohortConfirmation')}</Typography>

            {cohortDetail && (
              <div className={classes.siteInfo}>
                <Typography variant="body2">
                  <strong>{t('cohorts.cohort')}:</strong> {cohortDetail.name} ({cohortDetail.id})
                </Typography>
                <Typography variant="body2">
                  <strong>{t('cohorts.sites')}:</strong>{' '}
                  {t('cohorts.sitesWillBeRemoved', { count: visibleSitesCount })}
                </Typography>
              </div>
            )}

            <Typography variant="body2">{t('cohorts.deletionProcess')}</Typography>
            <Typography variant="body2" component="div" style={{ marginLeft: '16px' }}>
              {t('cohorts.deletionStep1')}
            </Typography>
            <Typography variant="body2" component="div" style={{ marginLeft: '16px' }}>
              {t('cohorts.deletionStep2')}
            </Typography>

            <Typography variant="body2" style={{ marginTop: '8px', fontStyle: 'italic' }}>
              {t('cohorts.sitesCanBeReassigned')}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <DefaultButton onClick={() => setDeleteCohortConfirmOpen(false)} disabled={loadingDelete}>
            {t('global.cancel', 'Cancel')}
          </DefaultButton>
          <PrimaryButton
            onClick={handleConfirmDeleteCohort}
            disabled={loadingDelete}
            color="primary"
          >
            {loadingDelete ? t('cohorts.deleting') : getDeleteButtonContent()}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CohortDetailDialog;
